/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Calendar-scoped query params and access guards for ViewAppointments.
 * @module logic/appointments/appointmentCalendarParams
 */

import { isBuyerUser } from "./appointmentListParams.js";

/**
 * Whether the page has enough context to load appointment list/calendar data.
 * @param {Object} options
 * @param {string|null|undefined} options.eventId
 * @param {Object|null|undefined} options.user
 * @param {Object|null|undefined} [options.selectedBuyerCompany]
 * @param {Object|null|undefined} [options.selectedBuyerExecutive]
 * @returns {boolean}
 */
export const canLoadAppointments = ({
  eventId,
  user,
  selectedBuyerCompany,
  selectedBuyerExecutive,
}) => {
  if (!eventId || !user) {
    return false;
  }

  if (isBuyerUser(user)) {
    return true;
  }

  if (user.tipoDeUsuario === "5") {
    return Boolean(
      selectedBuyerCompany?.value && selectedBuyerExecutive?.value,
    );
  }

  return false;
};

/**
 * Builds scoped and conflict API params for calendar fetches.
 * @param {Object} options
 * @param {Object} options.userData
 * @param {Object|null|undefined} options.selectedBuyerCompany
 * @param {Object|null|undefined} options.selectedBuyerExecutive
 * @returns {{ scopedParams: Object, conflictParams: Object }}
 */
export const buildCalendarScopeParams = ({
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
}) => {
  if (isBuyerUser(userData)) {
    const buyerUserId = userData._id || localStorage.getItem("userId");
    return {
      scopedParams: { buyerUserId },
      conflictParams: { buyerUserId },
    };
  }

  return {
    scopedParams: {
      buyerUserId: selectedBuyerExecutive.value,
      buyerCompanyId: selectedBuyerCompany.value,
    },
    conflictParams: {
      buyerUserId: selectedBuyerExecutive.value,
    },
  };
};

/**
 * Returns true for event admins until buyer company and executive are selected.
 * @param {Object|null|undefined} userData
 * @param {Object|null|undefined} selectedBuyerCompany
 * @param {Object|null|undefined} selectedBuyerExecutive
 * @returns {boolean}
 */
export const isBulkConfirmDisabled = (
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
) => {
  if (userData?.tipoDeUsuario === "5") {
    return !selectedBuyerCompany || !selectedBuyerExecutive;
  }
  return false;
};

/**
 * Filters requirements to those owned by the selected buyer executive.
 * @param {Object|null|undefined} participatingCompanyData
 * @param {string} executiveId
 * @returns {Array<Object>}
 */
export const filterRequirementsByExecutive = (
  participatingCompanyData,
  executiveId,
) =>
  (participatingCompanyData?.requerimientos || []).filter(
    (requirement) => String(requirement.idUsuarioComprador) === executiveId,
  );

/**
 * Returns calendar appointments when the current user scope allows viewing them.
 * @param {Object|null|undefined} userData
 * @param {Object|null|undefined} selectedBuyerCompany
 * @param {Object|null|undefined} selectedBuyerExecutive
 * @param {Array<Object>} appointments
 * @returns {Array<Object>}
 */
export const getScopedCalendarAppointments = (
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  appointments,
) => {
  if (userData?.tipoDeUsuario === "5") {
    if (!selectedBuyerCompany || !selectedBuyerExecutive) {
      return [];
    }
  }
  return appointments;
};
